import { Injectable } from '@angular/core';
import { Actions, concatLatestFrom, createEffect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { of } from 'rxjs';
import { catchError, exhaustMap, filter, map, switchMap } from 'rxjs/operators';
import { AccountManagersService } from 'src/app/accountmanagers/accountmanagers.service';
import { AmSubmittalsService } from 'src/app/accountmanagers/submittals/am-submittals.service';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import {
    getWdpTravelDetails,
    selectCandidateId,
    selectContractId,
    selectJobsLookups,
    selectSubmittalRemovalReasons,
    selectSubmittalStatuses,
    selectWdpGenderTypes,
    selectWdpStateCodes,
    selectWdpTravelPlanTypes,
    selectWdpTravelStatuses
} from '../account-managers.selectors';
import {
    amJobsForSubmittalsFail,
    amJobsForSubmittalsLoaded,
    amLoadJobsForSubmittals,
    amLoadJobsLookups,
    amLoadJobsLookupsFail,
    amLoadJobsLookupsSuccess,
    amLoadOfferRecruiters,
    amLoadOfferRecruitersError,
    amLoadOfferRecruitersSuccess,
    amLoadSubmittalRemovalReasons,
    amLoadSubmittalRemovalReasonsFail,
    amLoadSubmittalRemovalReasonsSuccess,
    amSendEmails,
    amSubmittalReassign,
    amSubmittalReassignFailure,
    amSubmittalReassignSuccess,
    getVendorInfoForSubmittals,
    getVendorInfoForSubmittalsSuccess,
    getVendorInfoForSubmittalsFailure,
    getSubmittalStatuses,
    getSubmittalStatusesSuccess,
    getSubmittalStatusesFailure,
    accountManagerDashboardAlertSuccess,
    accountManagerDashboardAlertFailure,
    amBulkRemoveFromLiveList,
    amBulkRemoveFromLiveListSuccess,
    amBulkRemoveFromLiveListFail,
    updateWfdPriority
} from '../actions/account-managers.actions';
import { loadNotes } from '../actions/workforce-notes.actions';

import {
    WorkforceDisruptionPlacementFlightBookingInformation,
    WorkforceDisruptionPlacementTravelDetails
} from 'src/app/workforce-disruption/shared/models/workforce-disruption.model';
import { WorkforceDisruptionService } from 'src/app/workforce-disruption/shared/services/workforce-disruption.service';
import { bookedCandidateDetailsActions } from '../actions/booked-candidate-details.actions';
import { SubmittalService } from 'src/app/recruiting/candidates/submittal/submittal-service.service';
import { HubConnectionService } from 'src/app/shared/services/realtime-connection.service';
import { AccountManagerDashboardAlert } from 'src/app/accountmanagers/dashboard/models/account-manager-dashboard.model';
import { AccountManagerHubMessage } from 'src/app/shared/services/realtime-constants';
import { WfdCandidateFacilityService } from 'src/app/shared/services/wfd-candidate-facility.service';

@Injectable()
export class AccountManagersEffects {
    getWfdHotelDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWfdHotelDetails),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getWfdHotelDetails(contractId).pipe(
                    map((hotelDetails) => bookedCandidateDetailsActions.getWfdHotelDetailsSuccess({ hotelDetails })),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWfdHotelDetailsFailure({ error }))
                    )
                );
            })
        );
    });

    updateWfdHotelDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWfdHotelDetails),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.updateWfdHotelDetails(contractId, action.hotelDetailsPayload).pipe(
                    map(() =>
                        bookedCandidateDetailsActions.updateWfdHotelDetailsSuccess({
                            hotelDetailsPayload: action.hotelDetailsPayload
                        })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.updateWfdHotelDetailsFailure({ error }))
                    )
                );
            })
        );
    });

    wfdHotelDetailsFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                bookedCandidateDetailsActions.getWfdHotelDetailsFailure,
                bookedCandidateDetailsActions.updateWfdHotelDetailsFailure
            ),
            map((action) => {
                const msg = action.type.includes('Get')
                    ? 'Failed to get Hotel Details data'
                    : 'Failed to update Hotel Details data';
                return showToasterMessage({ message: msg });
            })
        );
    });

    getWdpTravelDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpTravelDetails),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getContractTravelDetails(contractId).pipe(
                    map((travelDetails: WorkforceDisruptionPlacementTravelDetails) =>
                        bookedCandidateDetailsActions.getWdpTravelDetailsSuccess({ travelDetails })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpTravelDetailsFailure({ error }))
                    )
                );
            })
        );
    });

    updateWdpTravelDetails$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWdpTravelDetails),
            concatLatestFrom(() => [this._store.select(selectContractId), this._store.select(getWdpTravelDetails)]),
            switchMap(([action, contractId, travelDetail]) => {
                return this._wfdService.updateContractTravelDetails(contractId, action.travelDetails).pipe(
                    map(() => {
                        if (action.travelDetails.travelStatusId !== travelDetail.travelStatusId) {
                            this._store.dispatch(loadNotes());
                        }

                        localStorage.setItem('__wdpBus', (localStorage.getItem('__wdpBus') !== 'true').toString());

                        return bookedCandidateDetailsActions.updateWdpTravelDetailsSuccess({
                            travelDetails: action.travelDetails
                        });
                    }),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.updateWdpTravelDetailsFailure({ error }))
                    )
                );
            })
        );
    });

    wfdTravelDetailsFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                bookedCandidateDetailsActions.getWdpTravelDetailsFailure,
                bookedCandidateDetailsActions.updateWdpTravelDetailsFailure
            ),
            map((action) => {
                const msg = action.type.includes('Get')
                    ? 'Failed to get Travel Details data'
                    : 'Failed to update Travel Details data';
                return showToasterMessage({ message: msg });
            })
        );
    });

    getWdpFlightBookingInformation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpFlightBookingInformation),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getContractFlightBookingInformation(contractId).pipe(
                    map((flightBookingInformation: WorkforceDisruptionPlacementFlightBookingInformation[]) =>
                        bookedCandidateDetailsActions.getWdpFlightBookingInformationSuccess({
                            flightBookingInformation
                        })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpFlightBookingInformationFailure({ error }))
                    )
                );
            })
        );
    });

    updateWdpFlightBookingInformation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWdpFlightBookingInformation),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService
                    .updateContractFlightBookingInformation(contractId, action.flightBookingInformation)
                    .pipe(
                        map(() =>
                            bookedCandidateDetailsActions.updateWdpFlightBookingInformationSuccess({
                                flightBookingInformation: action.flightBookingInformation
                            })
                        ),
                        catchError((error: unknown) =>
                            of(bookedCandidateDetailsActions.updateWdpFlightBookingInformationFailure({ error }))
                        )
                    );
            })
        );
    });

    wfdFlightBookingInformationFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(
                bookedCandidateDetailsActions.getWdpFlightBookingInformationFailure,
                bookedCandidateDetailsActions.updateWdpFlightBookingInformationFailure
            ),
            map((action) => {
                const msg = action.type.includes('Get')
                    ? 'Failed to get Flight Booking Information data'
                    : 'Failed to update Flight Booking Information data';
                return showToasterMessage({ message: msg });
            })
        );
    });

    getContractPlacementInformation$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpPlacementInformation),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getContractPlacementInformation(contractId).pipe(
                    map((placementsInformation) =>
                        bookedCandidateDetailsActions.getWdpPlacementInformationSuccess({ placementsInformation })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpPlacementInformationFailure({ error }))
                    )
                );
            })
        );
    });

    wfdContractPlacementInformationFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpPlacementInformationFailure),
            map(() => {
                const msg = 'Failed to get Contract Placement Information data';
                return showToasterMessage({ message: msg });
            })
        );
    });

    getWdpCandidateProfile$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpCandidateProfile),
            concatLatestFrom(() => this._store.select(selectCandidateId)),
            switchMap(([action, candidateId]) => {
                return this._wfdService.getContractCandidateProfile(candidateId).pipe(
                    switchMap((candidateProfile) => {
                        return this._wfdService.getVerifiedDateOfBirth(candidateId).pipe(
                            map((verifiedDobDto) => ({
                                ...candidateProfile,
                                dateOfBirth: verifiedDobDto?.dateOfBirth
                                    ? (verifiedDobDto.dateOfBirth as unknown as Date)
                                    : null,
                                verifiedDobSource: verifiedDobDto?.source ?? null
                            })),
                            catchError(() =>
                                of({
                                    ...candidateProfile,
                                    dateOfBirth: null,
                                    verifiedDobSource: null
                                })
                            )
                        );
                    }),
                    map((candidateProfile) =>
                        bookedCandidateDetailsActions.getWdpCandidateProfileSuccess({ candidateProfile })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpCandidateProfileFailure({ error }))
                    )
                );
            })
        );
    });

    wfdCandidateProfileFailureToaster$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpCandidateProfileFailure),
            map(() => {
                const msg = 'Failed to get Candidate Profile data';
                return showToasterMessage({ message: msg });
            })
        );
    });

    getWfdAdditionalInfo$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWfdAdditionalInfo),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getWfdAdditionalInfo(contractId).pipe(
                    map((additionalInfo) =>
                        bookedCandidateDetailsActions.getWfdAdditionalInfoSuccess({ additionalInfo })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWfdAdditionalInfoError({ error }))
                    )
                );
            })
        );
    });

    updateWfdAdditionalInfo = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWfdAdditionalInfo),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.updateWfdAdditionalInfo(contractId, action.changes).pipe(
                    map((newItems) =>
                        bookedCandidateDetailsActions.updateWfdAdditionalInfoSuccess({
                            additionalInfo: newItems
                        })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.updateWfdAdditionalInfoError({ error }))
                    )
                );
            })
        );
    });

    getWfdFacilitySystemAdditionalInfo$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWfdFacilitySystemAdditionalInfo),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.getWfdFacilitySystemAdditionalInfo(contractId).pipe(
                    map((additionalInfo) =>
                        bookedCandidateDetailsActions.getWfdFacilitySystemAdditionalInfoSuccess({ additionalInfo })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWfdFacilitySystemAdditionalInfoError({ error }))
                    )
                );
            })
        );
    });

    updateWfdFacilitySystemAdditionalInfo = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWfdFacilitySystemAdditionalInfo),
            concatLatestFrom(() => this._store.select(selectContractId)),
            switchMap(([action, contractId]) => {
                return this._wfdService.updateWfdFacilitySystemAdditionalInfo(action.changes).pipe(
                    map((newItems) =>
                        bookedCandidateDetailsActions.updateWfdFacilitySystemAdditionalInfoSuccess({
                            additionalInfo: newItems
                        })
                    ),

                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.updateWfdFacilitySystemAdditionalInfoError({ error }))
                    )
                );
            })
        );
    });

    updateWfdFacilitySystemAdditionalInfoSuccess = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.updateWfdFacilitySystemAdditionalInfoSuccess),
            map(() => bookedCandidateDetailsActions.getWfdFacilitySystemAdditionalInfo())
        );
    });

    constructor(
        private readonly _actions$: Actions,
        private readonly _acctManagersService: AccountManagersService,
        private readonly _amSubmittalsService: AmSubmittalsService,
        private readonly _wfdService: WorkforceDisruptionService,
        private readonly _submittalService: SubmittalService,
        private readonly _hubConnectionService: HubConnectionService,
        private readonly _store: Store<{}>,
        private readonly _wfdCandidateFacilityService: WfdCandidateFacilityService
    ) {}

    reassignSubmittals$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amSubmittalReassign),
            switchMap((action) => {
                return this._acctManagersService
                    .reassignSubmittals({
                        submittalData: action.submittalData,
                        accountManagerId: action.accountManagerId,
                        fromAccountManagerId: action.fromAccountManagerId
                    })
                    .pipe(
                        map(() => amSubmittalReassignSuccess({ result: true })),
                        catchError((error: unknown) => of(amSubmittalReassignFailure({ error })))
                    );
            })
        );
    });

    loadJobsLookups$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amLoadJobsLookups),
            concatLatestFrom(() => this._store.select(selectJobsLookups)),
            switchMap(([action, lookups]) => {
                if (lookups) {
                    return of(amLoadJobsLookupsSuccess({ lookups }));
                }

                return this._acctManagersService.getGridLookups().pipe(
                    map((lookups) => amLoadJobsLookupsSuccess({ lookups })),
                    catchError((error: unknown) => of(amLoadJobsLookupsFail({ error })))
                );
            })
        );
    });

    loadJobsForSubmittlals$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amLoadJobsForSubmittals),
            switchMap((action) => {
                return this._amSubmittalsService.getJobsForSubmittals(action.submittalIds).pipe(
                    map((jobs) => amJobsForSubmittalsLoaded({ jobs })),
                    catchError((error: unknown) => of(amJobsForSubmittalsFail({ error })))
                );
            })
        );
    });

    sendEmailsToRecruiters$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amSendEmails),
            switchMap((action) => {
                return this._amSubmittalsService
                    .sendEmailsToJobsRecruiters(
                        action.jobIds,
                        action.apnSubject,
                        action.apnBody,
                        action.ayaSubject,
                        action.ayaBody,
                        action.sendTo
                    )
                    .pipe(
                        map((_) => showToasterMessage({ message: 'Email Sent', duration: 2000 })),
                        catchError((err: unknown) =>
                            of(showToasterMessage({ message: `Error Sending Emails: ${err}`, duration: 2000 }))
                        )
                    );
            })
        );
    });

    loadOfferRecruiters$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amLoadOfferRecruiters),
            switchMap((action) => {
                return this._amSubmittalsService.getOfferRecruiters().pipe(
                    map((recruiterIds) => amLoadOfferRecruitersSuccess({ recruiterIds })),
                    catchError((error: unknown) => of(amLoadOfferRecruitersError({ error })))
                );
            })
        );
    });

    loadSubmittalRemovalReasons$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amLoadSubmittalRemovalReasons),
            concatLatestFrom(() => this._store.select(selectSubmittalRemovalReasons)),
            switchMap(([_, submittalRemovalReasons]) => {
                if (submittalRemovalReasons && submittalRemovalReasons.length > 0) {
                    return of(amLoadSubmittalRemovalReasonsSuccess({ submittalRemovalReasons }));
                }
                return this._amSubmittalsService.getSubmittalRemovalReasons().pipe(
                    map((submittalRemovalReasons) => amLoadSubmittalRemovalReasonsSuccess({ submittalRemovalReasons })),
                    catchError((error: unknown) => of(amLoadSubmittalRemovalReasonsFail({ error })))
                );
            })
        );
    });

    bulkRemoveFromLiveList$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(amBulkRemoveFromLiveList),
            switchMap((action) => {
                return this._amSubmittalsService.bulkRemoveSubmittalJobs(action.submittalJobs).pipe(
                    map(() => amBulkRemoveFromLiveListSuccess()),
                    catchError((error: unknown) => of(amBulkRemoveFromLiveListFail({ error })))
                );
            })
        );
    });

    bulkRemoveFromLiveListToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(amBulkRemoveFromLiveListFail),
                map(() =>
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Job submittal(s) removal failed. Please try again later.',
                            duration: 3000
                        })
                    )
                )
            );
        },
        { dispatch: false }
    );

    loadRemovalReasonsToaster$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(amLoadSubmittalRemovalReasonsFail),
                map(() =>
                    this._store.dispatch(
                        showToasterMessage({
                            message: 'Cannot load removal reasons. Please try again later.',
                            duration: 3000
                        })
                    )
                )
            );
        },
        { dispatch: false }
    );

    getWdpTravelStatuses$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpTravelDetails),
            concatLatestFrom(() => this._store.select(selectWdpTravelStatuses)),
            exhaustMap(([action, travelStatuses]) => {
                if (travelStatuses && travelStatuses.length > 0) {
                    return of(bookedCandidateDetailsActions.getWdpTravelStatusesSuccess({ travelStatuses }));
                }

                return this._wfdService.getTravelStatuses().pipe(
                    map((travelStatuses) =>
                        bookedCandidateDetailsActions.getWdpTravelStatusesSuccess({ travelStatuses })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpTravelStatusesFailure({ error }))
                    )
                );
            })
        );
    });

    getWdpTravelPlanTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpTravelDetails),
            concatLatestFrom(() => this._store.select(selectWdpTravelPlanTypes)),
            exhaustMap(([action, travelPlanTypes]) => {
                if (travelPlanTypes && travelPlanTypes.length > 0) {
                    return of(bookedCandidateDetailsActions.getWdpTravelPlanTypesSuccess({ travelPlanTypes }));
                }

                return this._wfdService.getTravelPlanTypes().pipe(
                    map((travelPlanTypes) =>
                        bookedCandidateDetailsActions.getWdpTravelPlanTypesSuccess({ travelPlanTypes })
                    ),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpTravelPlanTypesFailure({ error }))
                    )
                );
            })
        );
    });

    getWdpGenderTypes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpTravelDetails),
            concatLatestFrom(() => this._store.select(selectWdpGenderTypes)),
            exhaustMap(([action, genderTypes]) => {
                if (genderTypes && genderTypes.length > 0) {
                    return of(bookedCandidateDetailsActions.getWdpGenderTypesSuccess({ genderTypes }));
                }

                return this._wfdService.getGenderTypes().pipe(
                    map((genderTypes) => bookedCandidateDetailsActions.getWdpGenderTypesSuccess({ genderTypes })),
                    catchError((error: unknown) =>
                        of(bookedCandidateDetailsActions.getWdpGenderTypesFailure({ error }))
                    )
                );
            })
        );
    });

    getWdpStateCodes$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(bookedCandidateDetailsActions.getWdpTravelDetails),
            concatLatestFrom(() => this._store.select(selectWdpStateCodes)),
            exhaustMap(([action, stateCodes]) => {
                if (stateCodes && stateCodes.length > 0) {
                    return of(bookedCandidateDetailsActions.getWdpStateCodesSuccess({ stateCodes }));
                }

                return this._wfdService.getStateCodes().pipe(
                    map((stateCodes) => bookedCandidateDetailsActions.getWdpStateCodesSuccess({ stateCodes })),
                    catchError((error: unknown) => of(bookedCandidateDetailsActions.getWdpStateCodesFailure({ error })))
                );
            })
        );
    });

    getVendorInfoForSubmittals$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getVendorInfoForSubmittals),
            switchMap((action) => {
                return this._amSubmittalsService.getSubmittalVendorInfo(action.userId).pipe(
                    map((vendorInfo) => getVendorInfoForSubmittalsSuccess({ vendorInfo })),
                    catchError((error: unknown) => of(getVendorInfoForSubmittalsFailure({ error })))
                );
            })
        );
    });

    getSubmittalStatuses$ = createEffect(() => {
        return this._actions$.pipe(
            ofType(getSubmittalStatuses),
            concatLatestFrom(() => this._store.select(selectSubmittalStatuses)),
            filter(([_, statuses]) => !statuses || statuses.length === 0),
            switchMap(() => {
                return this._submittalService.getSubmittalStatuses().pipe(
                    map((statuses) => getSubmittalStatusesSuccess({ statuses })),
                    catchError((error: unknown) => of(getSubmittalStatusesFailure({ error })))
                );
            })
        );
    });

    accountManagerDashboardAlert$ = createEffect(() =>
        this._hubConnectionService.coreHub
            .messagesOf<AccountManagerDashboardAlert>(AccountManagerHubMessage.ACCOUNT_MANAGER_DASHBOARD_ALERT)
            .pipe(
                map((alert) => accountManagerDashboardAlertSuccess({ data: alert })),
                catchError((er) => of(accountManagerDashboardAlertFailure({ error: er })))
            )
    );

    updateWfdPriority$ = createEffect(
        () => {
            return this._actions$.pipe(
                ofType(updateWfdPriority),
                switchMap((data) => {
                    return this._wfdCandidateFacilityService.bulkUpdateWfdPriorities(data);
                })
            );
        },
        { dispatch: false }
    );
}
